/**
 * Accounting Module API
 *
 * Provides CRUD operations for:
 * - Invoices (inv-xxx) - NOW USES GRAPHQL
 * - Invoice line items
 * - Movements/Journal entries (mov-xxx) - Still in-memory
 * - Chart of Accounts (categories) - Still in-memory
 * - Banking (stub for Phase 2)
 * - E-Invoice (stub for Phase 2)
 */

import {
  createEnvelope,
  createErrorEnvelope,
  type Envelope,
  type ISODate,
  type CentsAmount,
} from '@business-assistant/shared';

import { graphqlAccountingRepository } from '@business-assistant/shared/repositories';
import { auditTrailService } from '../audit/auditTrail';

// ============================================================================
// TYPES (imported from types/core.ts to avoid circular dependencies)
// ============================================================================

// Re-export types for backward compatibility
export type {
  InvoiceStatus,
  InvoiceLineItem,
  Invoice,
  MovementDirection,
  Movement,
  AccountCategory,
  InvoiceFilters,
  MovementFilters,
  AccountCategoryFilters,
} from '../types/core';

// Import types for local use
import type {
  InvoiceStatus,
  InvoiceLineItem,
  Invoice,
  MovementDirection,
  Movement,
  AccountCategory,
  ApiBankAccount as BankAccount,
  ApiBankTransaction as BankTransaction,
  ApiEInvoice as EInvoice,
  InvoiceFilters,
  MovementFilters,
  AccountCategoryFilters,
} from '../types/core';

// GraphQL types for mapping
import type { Invoice as GraphQLInvoice } from '@business-assistant/shared/repositories';

// Re-export stub types with their original names for backward compatibility
export type { ApiBankAccount as BankAccount, ApiBankTransaction as BankTransaction, ApiEInvoice as EInvoice } from '../types/core';

// ============================================================================
// TYPE MAPPING HELPERS
// ============================================================================

/**
 * Map GraphQL Invoice to Module Invoice
 */
function mapFromGraphQL(gqlInvoice: GraphQLInvoice): Invoice {
  return {
    id: gqlInvoice.id,
    customerId: gqlInvoice.customerId,
    customerName: gqlInvoice.customerName,
    customerVat: undefined,
    customerAddress: undefined,
    issueDate: gqlInvoice.date,
    dueDate: gqlInvoice.dueDate || gqlInvoice.date,
    items: (gqlInvoice.items || []).map(item => ({
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      vatRate: item.vatRate,
      netAmount: item.quantity * item.unitPrice,
      vatAmount: Math.round(item.quantity * item.unitPrice * item.vatRate),
      grossAmount: Math.round(item.quantity * item.unitPrice * (1 + item.vatRate)),
    })),
    notes: gqlInvoice.notes,
    status: gqlInvoice.status as InvoiceStatus,
    netTotal: gqlInvoice.subtotal || 0,
    vatTotal: gqlInvoice.vatTotal,
    grossTotal: gqlInvoice.total,
    paidAmount: 0,
    paidDate: undefined,
    paymentMethod: undefined,
    projectId: gqlInvoice.projectId,
    createdAt: gqlInvoice.createdAt,
    updatedAt: gqlInvoice.updatedAt,
  };
}

/**
 * Map Module Invoice to GraphQL Invoice input
 */
function mapToGraphQL(invoice: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>): Omit<GraphQLInvoice, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    number: `INV-${Date.now()}`,
    customerId: invoice.customerId || '',
    date: invoice.issueDate,
    dueDate: invoice.dueDate,
    direction: 'issued',
    customerName: invoice.customerName,
    subtotal: invoice.netTotal,
    vatTotal: invoice.vatTotal,
    total: invoice.grossTotal,
    status: invoice.status,
    items: invoice.items.map(item => ({
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      vatRate: item.vatRate,
    })),
    projectId: invoice.projectId,
    projectName: undefined,
    notes: invoice.notes,
  };
}

// ============================================================================
// IN-MEMORY STORAGE (for entities not yet in GraphQL)
// ============================================================================

const movementsStore = new Map<string, Movement>();
const accountCategoriesStore = new Map<string, AccountCategory>();
const bankAccountsStore = new Map<string, BankAccount>();
const bankTransactionsStore = new Map<string, BankTransaction>();
const eInvoicesStore = new Map<string, EInvoice>();

let movementCounter = 1;
let accountCounter = 1;
let bankAccountCounter = 1;
let bankTransactionCounter = 1;
let eInvoiceCounter = 1;

// Local cache for invoices (synced with GraphQL)
let invoicesCache: Invoice[] = [];
let invoicesCacheLoaded = false;

// ============================================================================
// INVOICE API (Uses GraphQL)
// ============================================================================

/**
 * Load invoices from GraphQL into cache
 */
async function loadInvoicesFromGraphQL(): Promise<void> {
  try {
    const gqlInvoices = await graphqlAccountingRepository.listInvoices();
    // Ensure we have an array before mapping
    invoicesCache = Array.isArray(gqlInvoices) ? gqlInvoices.map(mapFromGraphQL) : [];
    invoicesCacheLoaded = true;
  } catch (error) {
    console.warn('[Accounting API] Failed to load invoices from GraphQL:', error);
    // Keep using cache if available
  }
}

/**
 * Create a new invoice
 */
export async function createInvoice(
  data: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Envelope<Invoice>> {
  try {
    const gqlData = mapToGraphQL(data);
    const gqlInvoice = await graphqlAccountingRepository.createInvoice(gqlData);
    const invoice = mapFromGraphQL(gqlInvoice);

    // Update cache
    invoicesCache.push(invoice);

    // Audit log
    await auditTrailService.log({
      userId: 'system',
      action: 'CREATE',
      entityType: 'INVOICE',
      entityId: invoice.id,
      newValue: invoice,
    });

    return createEnvelope(invoice);
  } catch (error) {
    console.error('[Accounting API] Failed to create invoice:', error);
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to create invoice');
  }
}

/**
 * Get invoice by ID
 */
export async function getInvoice(id: string): Promise<Envelope<Invoice>> {
  try {
    // Try from cache first
    let invoice = invoicesCache.find(inv => inv.id === id);

    if (!invoice) {
      // Reload from GraphQL
      await loadInvoicesFromGraphQL();
      invoice = invoicesCache.find(inv => inv.id === id);
    }

    if (!invoice) {
      return createErrorEnvelope('NOT_FOUND', `Invoice ${id} not found`);
    }

    return createEnvelope(invoice);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to get invoice');
  }
}

/**
 * Update invoice
 */
export async function updateInvoice(
  id: string,
  updates: Partial<Omit<Invoice, 'id' | 'createdAt'>>
): Promise<Envelope<void>> {
  try {
    const existingInvoice = invoicesCache.find(inv => inv.id === id);

    if (!existingInvoice) {
      return createErrorEnvelope('NOT_FOUND', `Invoice ${id} not found`);
    }

    // Map updates to GraphQL format
    const gqlUpdates: Record<string, unknown> = {};
    if (updates.customerName !== undefined) gqlUpdates.customerName = updates.customerName;
    if (updates.issueDate !== undefined) gqlUpdates.date = updates.issueDate;
    if (updates.dueDate !== undefined) gqlUpdates.dueDate = updates.dueDate;
    if (updates.status !== undefined) gqlUpdates.status = updates.status;
    if (updates.netTotal !== undefined) gqlUpdates.subtotal = updates.netTotal;
    if (updates.vatTotal !== undefined) gqlUpdates.vatTotal = updates.vatTotal;
    if (updates.grossTotal !== undefined) gqlUpdates.total = updates.grossTotal;
    if (updates.notes !== undefined) gqlUpdates.notes = updates.notes;
    if (updates.items !== undefined) {
      gqlUpdates.items = updates.items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        vatRate: item.vatRate,
      }));
    }

    const gqlInvoice = await graphqlAccountingRepository.updateInvoice(id, gqlUpdates as never);
    const updatedInvoice = mapFromGraphQL(gqlInvoice);

    // Update cache
    const index = invoicesCache.findIndex(inv => inv.id === id);
    if (index !== -1) {
      invoicesCache[index] = updatedInvoice;
    }

    // Audit log
    await auditTrailService.log({
      userId: 'system',
      action: 'UPDATE',
      entityType: 'INVOICE',
      entityId: id,
      previousValue: existingInvoice,
      newValue: updatedInvoice,
    });

    return createEnvelope(undefined);
  } catch (error) {
    console.error('[Accounting API] Failed to update invoice:', error);
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to update invoice');
  }
}

/**
 * Delete invoice
 */
export async function deleteInvoice(id: string): Promise<Envelope<void>> {
  try {
    const invoice = invoicesCache.find(inv => inv.id === id);

    if (!invoice) {
      return createErrorEnvelope('NOT_FOUND', `Invoice ${id} not found`);
    }

    await graphqlAccountingRepository.deleteInvoice(id);

    // Update cache
    invoicesCache = invoicesCache.filter(inv => inv.id !== id);

    // Audit log
    await auditTrailService.log({
      userId: 'system',
      action: 'DELETE',
      entityType: 'INVOICE',
      entityId: id,
      previousValue: invoice,
    });

    return createEnvelope(undefined);
  } catch (error) {
    console.error('[Accounting API] Failed to delete invoice:', error);
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to delete invoice');
  }
}

/**
 * List invoices with optional filtering
 */
export async function listInvoices(
  filters?: InvoiceFilters
): Promise<Envelope<Invoice[]>> {
  try {
    // Load from GraphQL if not cached
    if (!invoicesCacheLoaded) {
      await loadInvoicesFromGraphQL();
    }

    let invoices = [...invoicesCache];

    if (filters) {
      // Filter by customer ID
      if (filters.customerId) {
        invoices = invoices.filter((inv) => inv.customerId === filters.customerId);
      }

      // Filter by status
      if (filters.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        invoices = invoices.filter((inv) => statuses.includes(inv.status));
      }

      // Filter by date range
      if (filters.startDate) {
        invoices = invoices.filter((inv) => inv.issueDate >= filters.startDate!);
      }
      if (filters.endDate) {
        invoices = invoices.filter((inv) => inv.issueDate <= filters.endDate!);
      }

      // Filter by project ID
      if (filters.projectId) {
        invoices = invoices.filter((inv) => inv.projectId === filters.projectId);
      }

      // Filter overdue only
      if (filters.overdueOnly) {
        const now = new Date().toISOString();
        invoices = invoices.filter(
          (inv) => inv.status !== 'paid' && inv.dueDate < now
        );
      }

      // Search in customer name or notes
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        invoices = invoices.filter(
          (inv) =>
            inv.customerName.toLowerCase().includes(searchLower) ||
            (inv.notes && inv.notes.toLowerCase().includes(searchLower))
        );
      }
    }

    // Sort by issue date descending
    invoices.sort((a, b) => b.issueDate.localeCompare(a.issueDate));

    return createEnvelope(invoices);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to list invoices');
  }
}

/**
 * Calculate invoice totals from line items
 */
export function calculateInvoiceTotals(items: InvoiceLineItem[]): {
  netTotal: CentsAmount;
  vatTotal: CentsAmount;
  grossTotal: CentsAmount;
} {
  let netTotal = 0;
  let vatTotal = 0;
  let grossTotal = 0;

  for (const item of items) {
    netTotal += item.netAmount;
    vatTotal += item.vatAmount;
    grossTotal += item.grossAmount;
  }

  return { netTotal, vatTotal, grossTotal };
}

/**
 * Calculate line item amounts
 */
export function calculateLineItemAmounts(
  quantity: number,
  unitPrice: CentsAmount,
  vatRate: number
): {
  netAmount: CentsAmount;
  vatAmount: CentsAmount;
  grossAmount: CentsAmount;
} {
  const netAmount = Math.round(quantity * unitPrice);
  const vatAmount = Math.round(netAmount * vatRate);
  const grossAmount = netAmount + vatAmount;

  return { netAmount, vatAmount, grossAmount };
}

/**
 * Record payment for invoice
 */
export async function recordInvoicePayment(
  id: string,
  amount: CentsAmount,
  paymentDate: ISODate,
  paymentMethod: string
): Promise<Envelope<void>> {
  const invoice = invoicesCache.find(inv => inv.id === id);

  if (!invoice) {
    return createErrorEnvelope('NOT_FOUND', `Invoice ${id} not found`);
  }

  const newPaidAmount = invoice.paidAmount + amount;
  const isPaidInFull = newPaidAmount >= invoice.grossTotal;

  const updatedInvoice: Invoice = {
    ...invoice,
    paidAmount: newPaidAmount,
    paidDate: isPaidInFull ? paymentDate : invoice.paidDate,
    paymentMethod: isPaidInFull ? paymentMethod : invoice.paymentMethod,
    status: isPaidInFull ? 'paid' : invoice.status,
    updatedAt: new Date().toISOString(),
  };

  // Update GraphQL (status change)
  try {
    await graphqlAccountingRepository.updateInvoice(id, {
      status: updatedInvoice.status,
    } as never);
  } catch (error) {
    console.warn('[Accounting API] Failed to sync payment to GraphQL:', error);
  }

  // Update cache
  const index = invoicesCache.findIndex(inv => inv.id === id);
  if (index !== -1) {
    invoicesCache[index] = updatedInvoice;
  }

  // Audit log
  await auditTrailService.log({
    userId: 'system',
    action: 'UPDATE',
    entityType: 'INVOICE',
    entityId: id,
    previousValue: invoice,
    newValue: updatedInvoice,
    metadata: { operation: 'recordPayment', amount, paymentDate, paymentMethod },
  });

  return createEnvelope(undefined);
}

/**
 * Get overdue invoices
 */
export async function getOverdueInvoices(): Promise<Envelope<Invoice[]>> {
  return listInvoices({ overdueOnly: true });
}

/**
 * Get invoice statistics
 */
export async function getInvoiceStats(): Promise<
  Envelope<{
    totalRevenue: CentsAmount;
    totalPaid: CentsAmount;
    totalOutstanding: CentsAmount;
    overdueCount: number;
    overdueAmount: CentsAmount;
  }>
> {
  try {
    // Load from GraphQL if not cached
    if (!invoicesCacheLoaded) {
      await loadInvoicesFromGraphQL();
    }

    const invoices = [...invoicesCache];
    const now = new Date().toISOString();

    let totalRevenue = 0;
    let totalPaid = 0;
    let totalOutstanding = 0;
    let overdueCount = 0;
    let overdueAmount = 0;

    for (const invoice of invoices) {
      if (invoice.status !== 'cancelled') {
        totalRevenue += invoice.grossTotal;
        totalPaid += invoice.paidAmount;

        const outstanding = invoice.grossTotal - invoice.paidAmount;
        if (outstanding > 0) {
          totalOutstanding += outstanding;

          if (invoice.dueDate < now && invoice.status !== 'paid') {
            overdueCount++;
            overdueAmount += outstanding;
          }
        }
      }
    }

    return createEnvelope({
      totalRevenue,
      totalPaid,
      totalOutstanding,
      overdueCount,
      overdueAmount,
    });
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to calculate invoice stats');
  }
}

// ============================================================================
// MOVEMENT API
// ============================================================================

/**
 * Create a new movement
 */
export async function createMovement(
  data: Omit<Movement, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Envelope<Movement>> {
  try {
    const id = `mov-${String(movementCounter++).padStart(6, '0')}`;
    const now = new Date().toISOString();

    const movement: Movement = {
      ...data,
      id,
      createdAt: now,
      updatedAt: now,
    };

    movementsStore.set(id, movement);

    // Audit log
    await auditTrailService.log({
      userId: 'system',
      action: 'CREATE',
      entityType: 'TRANSACTION',
      entityId: id,
      newValue: movement,
    });

    return createEnvelope(movement);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to create movement');
  }
}

/**
 * Get movement by ID
 */
export async function getMovement(id: string): Promise<Envelope<Movement>> {
  const movement = movementsStore.get(id);

  if (!movement) {
    return createErrorEnvelope('NOT_FOUND', `Movement ${id} not found`);
  }

  return createEnvelope(movement);
}

/**
 * Update movement
 */
export async function updateMovement(
  id: string,
  updates: Partial<Omit<Movement, 'id' | 'createdAt'>>
): Promise<Envelope<void>> {
  const movement = movementsStore.get(id);

  if (!movement) {
    return createErrorEnvelope('NOT_FOUND', `Movement ${id} not found`);
  }

  const updatedMovement: Movement = {
    ...movement,
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  movementsStore.set(id, updatedMovement);

  // Audit log
  await auditTrailService.log({
    userId: 'system',
    action: 'UPDATE',
    entityType: 'TRANSACTION',
    entityId: id,
    previousValue: movement,
    newValue: updatedMovement,
  });

  return createEnvelope(undefined);
}

/**
 * Delete movement
 */
export async function deleteMovement(id: string): Promise<Envelope<void>> {
  const movement = movementsStore.get(id);

  if (!movement) {
    return createErrorEnvelope('NOT_FOUND', `Movement ${id} not found`);
  }

  const deleted = movementsStore.delete(id);

  if (deleted) {
    // Audit log
    await auditTrailService.log({
      userId: 'system',
      action: 'DELETE',
      entityType: 'TRANSACTION',
      entityId: id,
      previousValue: movement,
    });
  }

  return createEnvelope(undefined);
}

/**
 * List movements with optional filtering
 */
export async function listMovements(
  filters?: MovementFilters
): Promise<Envelope<Movement[]>> {
  try {
    let movements = Array.from(movementsStore.values());

    if (filters) {
      // Filter by date range
      if (filters.startDate) {
        movements = movements.filter((mov) => mov.date >= filters.startDate!);
      }
      if (filters.endDate) {
        movements = movements.filter((mov) => mov.date <= filters.endDate!);
      }

      // Filter by category
      if (filters.category) {
        movements = movements.filter((mov) => mov.category === filters.category);
      }

      // Filter by direction
      if (filters.direction) {
        movements = movements.filter((mov) => mov.direction === filters.direction);
      }

      // Filter by account
      if (filters.account) {
        movements = movements.filter((mov) => mov.account === filters.account);
      }

      // Filter by project ID
      if (filters.projectId) {
        movements = movements.filter((mov) => mov.projectId === filters.projectId);
      }

      // Search in description or notes
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        movements = movements.filter(
          (mov) =>
            mov.description.toLowerCase().includes(searchLower) ||
            (mov.notes && mov.notes.toLowerCase().includes(searchLower))
        );
      }
    }

    // Sort by date descending
    movements.sort((a, b) => b.date.localeCompare(a.date));

    return createEnvelope(movements);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to list movements');
  }
}

/**
 * Get movement statistics
 */
export async function getMovementStats(
  startDate?: ISODate,
  endDate?: ISODate
): Promise<
  Envelope<{
    totalIn: CentsAmount;
    totalOut: CentsAmount;
    balance: CentsAmount;
    byCategory: Record<string, { in: CentsAmount; out: CentsAmount }>;
  }>
> {
  try {
    const movements = Array.from(movementsStore.values()).filter((mov) => {
      if (startDate && mov.date < startDate) return false;
      if (endDate && mov.date > endDate) return false;
      return true;
    });

    let totalIn = 0;
    let totalOut = 0;
    const byCategory: Record<string, { in: CentsAmount; out: CentsAmount }> = {};

    for (const movement of movements) {
      if (movement.direction === 'in') {
        totalIn += movement.amount;
        byCategory[movement.category] = byCategory[movement.category] || {
          in: 0,
          out: 0,
        };
        byCategory[movement.category].in += movement.amount;
      } else {
        totalOut += movement.amount;
        byCategory[movement.category] = byCategory[movement.category] || {
          in: 0,
          out: 0,
        };
        byCategory[movement.category].out += movement.amount;
      }
    }

    return createEnvelope({
      totalIn,
      totalOut,
      balance: totalIn - totalOut,
      byCategory,
    });
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to calculate movement stats');
  }
}

// ============================================================================
// CHART OF ACCOUNTS API
// ============================================================================

/**
 * Create account category
 */
export async function createAccountCategory(
  data: Omit<AccountCategory, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Envelope<AccountCategory>> {
  try {
    const id = `acc-${String(accountCounter++).padStart(6, '0')}`;
    const now = new Date().toISOString();

    const category: AccountCategory = {
      ...data,
      id,
      createdAt: now,
      updatedAt: now,
    };

    accountCategoriesStore.set(id, category);
    return createEnvelope(category);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to create account category');
  }
}

/**
 * Get account category by ID
 */
export async function getAccountCategory(
  id: string
): Promise<Envelope<AccountCategory>> {
  const category = accountCategoriesStore.get(id);

  if (!category) {
    return createErrorEnvelope('NOT_FOUND', `Account category ${id} not found`);
  }

  return createEnvelope(category);
}

/**
 * Update account category
 */
export async function updateAccountCategory(
  id: string,
  updates: Partial<Omit<AccountCategory, 'id' | 'createdAt'>>
): Promise<Envelope<void>> {
  const category = accountCategoriesStore.get(id);

  if (!category) {
    return createErrorEnvelope('NOT_FOUND', `Account category ${id} not found`);
  }

  const updatedCategory: AccountCategory = {
    ...category,
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  accountCategoriesStore.set(id, updatedCategory);
  return createEnvelope(undefined);
}

/**
 * Delete account category
 */
export async function deleteAccountCategory(id: string): Promise<Envelope<void>> {
  const deleted = accountCategoriesStore.delete(id);

  if (!deleted) {
    return createErrorEnvelope('NOT_FOUND', `Account category ${id} not found`);
  }

  return createEnvelope(undefined);
}

/**
 * List account categories with optional filtering
 */
export async function listAccountCategories(
  filters?: AccountCategoryFilters
): Promise<Envelope<AccountCategory[]>> {
  try {
    let categories = Array.from(accountCategoriesStore.values());

    if (filters) {
      // Filter by type
      if (filters.type) {
        categories = categories.filter((cat) => cat.type === filters.type);
      }

      // Filter by parent ID
      if (filters.parentId !== undefined) {
        categories = categories.filter((cat) => cat.parentId === filters.parentId);
      }

      // Filter active only
      if (filters.activeOnly) {
        categories = categories.filter((cat) => cat.isActive);
      }
    }

    // Sort by code
    categories.sort((a, b) => a.code.localeCompare(b.code));

    return createEnvelope(categories);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to list account categories');
  }
}

/**
 * Get account category tree
 */
export async function getAccountCategoryTree(): Promise<
  Envelope<(AccountCategory & { children: AccountCategory[] })[]>
> {
  try {
    const categories = Array.from(accountCategoriesStore.values());

    // Build tree structure
    const rootCategories = categories.filter((cat) => !cat.parentId);
    const tree = rootCategories.map((root) => ({
      ...root,
      children: categories.filter((cat) => cat.parentId === root.id),
    }));

    return createEnvelope(tree);
  } catch (error) {
    return createErrorEnvelope(
      'INTERNAL_ERROR',
      'Failed to build account category tree'
    );
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Clear all data
 */
export function clearAllData(): void {
  // Clear local caches
  invoicesCache = [];
  invoicesCacheLoaded = false;
  movementsStore.clear();
  accountCategoriesStore.clear();
  bankAccountsStore.clear();
  bankTransactionsStore.clear();
  eInvoicesStore.clear();

  movementCounter = 1;
  accountCounter = 1;
  bankAccountCounter = 1;
  bankTransactionCounter = 1;
  eInvoiceCounter = 1;
}

/**
 * Seed sample data - DISABLED
 * Data should come from GraphQL database only
 */
export async function seedSampleData(): Promise<void> {
  // Disabled - no demo data
  console.warn('[Accounting API] seedSampleData is disabled. Use real data from database.');
}

// ============================================================================
// RE-EXPORTS FROM SPECIALIZED MODULES
// ============================================================================

// Customers API
export {
  createCustomer,
  getCustomer,
  updateCustomer,
  deleteCustomer,
  listCustomers,
  getCustomerWithStats,
  deactivateCustomer,
  reactivateCustomer,
  getCustomerByVat,
  getCustomerByFiscalCode,
  getCustomerDisplayName,
  getCustomerTaxId,
  isValidItalianVAT,
  isValidItalianFiscalCode,
  isValidSDICode,
  clearCustomers,
  seedSampleCustomers,
} from './customers';

// Banking API
export {
  createBankAccount,
  getBankAccount,
  updateBankAccount,
  deleteBankAccount,
  listBankAccounts,
  deactivateBankAccount,
  createBankTransaction,
  getBankTransaction,
  updateBankTransaction,
  deleteBankTransaction,
  listBankTransactions,
  matchTransactionToInvoice,
  matchTransactionToMovement,
  unmatchTransaction,
  startReconciliation,
  getReconciliation,
  reconcileTransaction,
  addReconciliationAdjustment,
  completeReconciliation,
  listReconciliations,
  maskIBAN,
  maskAccountNumber,
  calculateMatchConfidence,
  clearBankingData,
  seedSampleBankingData,
} from './banking';

// E-Invoice API
export {
  createEInvoice,
  getEInvoice,
  getEInvoiceByInvoiceId,
  updateEInvoice,
  deleteEInvoice,
  listEInvoices,
  transmitToSDI,
  recordSDINotification,
  getSDINotifications,
  signEInvoice,
  archiveEInvoice,
  generateFatturaPAXML,
  getInvoiceTypeDescription,
  getSDIStatusDescription,
  getEInvoiceStatusColor,
  canEditEInvoice,
  canRetransmitEInvoice,
  calculateXMLHash,
  validateFatturaPAXML,
  clearEInvoiceData,
  getEInvoiceStats,
} from './einvoices';

// Payments API
export {
  createPayment,
  getPayment,
  getPaymentsForInvoice,
  listPayments,
  deletePayment,
  createDueDate,
  getDueDate,
  getDueDateByInvoiceId,
  updateDueDate,
  listDueDates,
  getOverdueDueDates,
  getUpcomingDueDates,
  markReminderSent,
  getDueDateStats,
  getPaymentStats,
  getPaymentMethodLabel,
  getPaymentTermsLabel,
  calculateDueDate,
  getDaysFromDueDate,
  isOverdue,
  getDueDateStatusColor,
  getPaymentStatusColor,
  clearPaymentData,
  seedSamplePaymentData,
} from './payments';
